<template>
    <v-card elevation="0">
        <v-card-title class="mt-n2">
            <div class="headline">{{$t('app_drawer.user')}}</div>
            <v-spacer></v-spacer>
            <v-btn v-if="isSuperAdmin" color="primary" @click="$router.push({name: 'user-add'})">
                {{$t('user.create')}}
                <v-icon right>mdi-account-plus</v-icon>
            </v-btn>
        </v-card-title>
        <v-card-text>
            <v-row>
                <v-col>
                    <v-text-field v-model="searchEmail"
                                append-icon="mdi-magnify"
                                :label="$t('user.search')"
                                single-line
                                hide-details
                                @keydown.enter="searching('email')">
                    </v-text-field>
                </v-col>
                <v-col>
                    <v-select clearable
                                :label="$t('user.company')"
                                :placeholder="$t('user.company')"
                                :items="companyOptions"
                                v-model="filterByCompanyId"
                                @change="filtering('company')">
                    </v-select>
                </v-col>
                <v-col>
                    <v-select clearable
                                :label="$t('user.role')"
                                :placeholder="$t('user.role')"
                                :items="permissionOptions"
                                v-model="filterByPermission"
                                @change="filtering('permission')">
                    </v-select>
                </v-col>
            </v-row>
            <v-data-table :headers="headers"
                        :items="formatedDatas"
                        :server-items-length="total"
                        :options.sync="options"
                        :loading="loading"
                        :footer-props="footer">
                <template v-slot:item.eventName="{item}">
                    <div v-for="(item, i) in item.eventName" :key="i" >{{item.name}}</div>
                </template>
                <template v-slot:item.actions="{item}">
                    <v-btn color="primary"
                        @click="$router.push({name: 'user-edit', params: {userId:item.id}})">
                        {{$t('edit')}}
                        <v-icon right>mdi-account-edit</v-icon>
                    </v-btn>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script>
    import {useFind} from "feathers-vuex";
    import {ref, computed, watch} from '@vue/composition-api'
    import {DateTime} from 'luxon'
    import eConfig from "../../evention-config";
    import _ from "lodash";

    export default {
        name: 'UserList',
        setup(props, {root}) {
            const initialized = ref(true);
            const {User, Company} = root.$FeathersVuex.api;
            
            const INITIAL_SORT = {createdAt: -1};
            const INITIAL_LIMIT = 10;
            
            const searchEmail = ref('');
            const filterByCompanyId = ref(undefined);
            const filterByPermission = ref(undefined);
            const sortBy = ref(null);
            const orderBy = ref(null);

            const search = ref({
                $search: searchEmail.value,
            });
            const pagination = ref({
                $limit: INITIAL_LIMIT,
                $skip: 0,
            });
            const query = ref({
                $sort: _.cloneDeep(INITIAL_SORT),
            });
            
            const options = ref({
                page: 1,
                itemsPerPage: pagination.value.$limit,

                // sortBy: string[],
                // sortDesc: boolean[],
                // groupBy: string[],
                // groupDesc: boolean[],
                // multiSort: boolean,
                // mustSort: boolean,
            });

            const loading = ref(true);
            const total = ref(0);
            const footer = ref({
                'items-per-page-options': [INITIAL_LIMIT, 25, 50, 100],
            });

            const formatedDatas = ref([]);
            const permissionMaps = [];
            for (let [key, value] of Object.entries(eConfig.user.permission)) {
                permissionMaps[value] = key;
            }

            const isSuperAdmin = computed(() => {
                return (root.$store.state.auth.user && root.$store.state.auth.user.permissions === eConfig.user.permission.super);
            });

            const usersParams = computed({
                get: () => {
                    Object.assign(query.value, pagination.value);
                    Object.assign(query.value, search.value);
                    if (filterByCompanyId.value)
                        Object.assign(query.value, {companyId: filterByCompanyId.value});
                    else
                        delete query.value.companyId;
                    if (filterByPermission.value)
                        Object.assign(query.value, {permissions: filterByPermission.value});
                    else
                        delete query.value.permissions;
                    if (sortBy.value && orderBy.value) {
                        delete query.value.$sort;
                        Object.assign(query.value, {$sort: {[sortBy.value]: orderBy.value}} );
                    }

                    return { query: query.value, qid: 'userListPage'};
                },
            });
            const usersData = useFind({model: User, params: usersParams});

            const companyParams = computed(() => {
                return {
                    query: {
                        $sort: {
                            name: 1,
                        }
                    }
                }
            });
            const companyData = useFind({model: Company, params: companyParams});
            const companyOptions = computed(() => {
                return companyData.items.value.map(c => {
                    return {text: c.name, value: c.id}
                });
            });
            const permissionOptions = computed(()=> {
                return Object.keys(eConfig.user.permission).map(k => {
                    return {text: root.$t(`user.permission.${k}`) , value: eConfig.user.permission[k]}
                });
            });

            const latestQueryData = computed(()=> {
                if (usersData && usersData.latestQuery.value) {
                    return usersData.latestQuery.value.response.data;
                }
            });

            /*const userPermission = computed(()=>{
                if (root.$store.state.auth.user)
                    return root.$store.state.auth.user.permissions;
                return null;
            });*/


            const searching = (type) => {        
                if (type === 'email') {
                    pagination.value.$skip = 0;
                    options.value.page = 1;

                    search.value.$search = searchEmail.value;
                }
            };
            const filtering = (type) => {
                if (type === 'company') {
                    if (filterByCompanyId.value) {
                        pagination.value.$skip = 0;
                        options.value.page = 1;
                    }
                }
                if (type === 'permission') {
                    if (filterByPermission.value) {
                        pagination.value.$skip = 0;
                        options.value.page = 1;
                    }
                }
            }
            const formatingData = (datas) => {
                return datas.map(user=>{
                    return {
                        id: user.id,
                        email: user.email,
                        permissions: root.$t('user.permission.'+permissionMaps[user.permissions]),
                        companyName: (user.company)?user.company.name:"",
                        eventName: user.events,
                        createdAt: DateTime.fromISO(user.createdAt).toFormat('yyyy-MM-dd HH:mm'),
                        updatedAt: DateTime.fromISO(user.updatedAt).toFormat('yyyy-MM-dd HH:mm'),
                    }
                });
            }

            watch(usersData.isPending, (isPending, prevIsPending) => {
                if (!isPending) {
                    initialized.value = false;
                    loading.value = false;
                } else {
                    loading.value = true;
                }
            });

            watch(latestQueryData, (newValue, oldValue) => {
                if (newValue) { //usersData.latestQuery.value
                    formatedDatas.value = formatingData(newValue);
                    total.value = usersData.paginationData.value[usersParams.value.qid].mostRecent.total;
                }
            });

            watch(options, (newValue, oldValue) => {
                if (initialized.value) {
                    if (usersData && usersData.paginationData.value[usersParams.value.qid]) {
                        total.value = usersData.paginationData.value[usersParams.value.qid].mostRecent.total;                
                    }
                } else {
                    const changedList = _.reduce(newValue, function(result, value, key) {
                        return _.isEqual(value, oldValue[key]) ?
                            result : result.concat(key);
                    }, [])

                    changedList.forEach((changed)=>{
                        switch(changed) {
                            case "page":
                                if (usersData && usersData.paginationData.value[usersParams.value.qid]) {
                                    pagination.value.$skip = (newValue.page-1) * newValue.itemsPerPage;
                                }
                                break;
                            case "itemsPerPage":
                                pagination.value.$limit = newValue.itemsPerPage;
                                break;
                            case "sortBy":
                            case "sortDesc":
                                if (newValue.sortBy.length > 0 && newValue.sortDesc.length > 0) {
                                    let sortField = newValue.sortBy[0];
                                    if (newValue.sortBy[0] === 'companyName')
                                        sortField = 'companyId';
                                        
                                    sortBy.value = sortField;
                                    orderBy.value = (newValue.sortDesc[0]) ? -1 : 1;
                                } else {
                                    sortBy.value = Object.keys(INITIAL_SORT)[0];
                                    orderBy.value = Object.values(INITIAL_SORT)[0];
                                }
                                break;
                            default:
                                console.error(`Invalid Action: ${changed}`);
                                break;
                        }
                    });
                }
            }, {deep: true});

            /*watch(userPermission, (newValue, oldValue)=>{
                if (newValue !== null)
                    if (root.$store.state.auth.user && (eConfig.user.permission.super === newValue)) {
                        showCreateBtn.value = true;
                    }
            });*/

            // Data Table
            const headers = ref([
                {text: root.$t('user.email'), value: 'email'},
                {text: root.$t('user.role'), value: 'permissions'},
                {text: root.$t('user.company'), value: 'companyName'},
                {text: root.$t('user.event'), value: 'eventName', sortable: false},
                {text: root.$t('user.created_at'), value: 'createdAt'},
                {text: root.$t('user.updated_at'), value: 'updatedAt'},
                {text: '', value: 'actions', filterable: false, width: '120', align: 'end'},
            ]);


            // 4. Return the data, named as you prefer
            return {
                searchEmail,
                filterByCompanyId,
                filterByPermission,
                options,

                headers,
                formatedDatas,

                total,
                loading,
                footer,

                isSuperAdmin,

                companyOptions,
                permissionOptions,

                searching,
                filtering,
            }
        },
    }
</script>
